import { CreateBookingRequest, CreateBookingResponse, RescheduleBookingResponse } from '@/types/scheduling';
import { getUserById, getUserProfile } from '../../data/users';
import { getProductById } from '../../data/products';
import { getLocationById } from '../../data/locations';
import { createBooking as createBookingInDb, updateBooking, getBookingById, updateBookingByUid } from '../../data/bookings';
import { syncStaffFromCalBooking } from '../../business/staffSync';
import { getUserTimeZone, formatBookingTitle } from './calUtils';

/**
 * Booking data with Calendly fields for legacy support
 */
export interface BookingWithCalendlyFields {
  user_id: string;
  product_id?: string | null;
  location_id?: string | null;
  booking_calendly_uri?: string | null;
  shooting_id?: string | null;
  booking_uid?: string | null;
}

/**
 * Parameters for creating a temporary booking
 */
export interface CreateTempBookingParams {
  userId: string;
  slotStart: string;
  productId: string;
  eventTypeSlug: string;
  locationId?: string;
  existingShootingId?: string | null;
  metadata?: Record<string, any>;
  directorId?: string;
}

/**
 * Parameters for creating a booking payload
 */
export interface CreateBookingPayloadParams {
  slotStart: string;
  userName: string;
  userEmail: string;
  productId: string;
  userId: string;
  eventTypeSlug: string;
  teamSlug?: string;
  eventTypeId?: number;
  username?: string;
  metadata?: Record<string, any>;
  prsnlBookingId?: string;
}

/**
 * Parameters for saving booking and syncing staff
 */
export interface SaveBookingAndSyncStaffParams {
  calBookingData: any;
  userId: string;
  slotStart: string;
  userName: string;
  userEmail: string;
  productId: string;
  eventTypeSlug: string;
  locationId?: string | null;
  meetingUrl?: string | null;
  shootingId?: string | null;
}

/**
 * Parameters for creating a mock booking
 */
export interface CreateMockBookingParams {
  userId: string;
  slotStart: string;
  userName: string;
  userEmail: string;
  productId: string;
  mockBookingId: number;
  mockUid: string;
  eventTypeSlug: string;
  locationId?: string;
}

/**
 * Result from creating a temporary booking
 */
export type TempBookingResult =
  | {
      success: true;
      bookingId: string;
      tempBookingUid: string;
    }
  | {
      success: false;
      error: CreateBookingResponse;
    };

/**
 * Creates a temporary booking in the database pending Cal.com confirmation.
 * When directorId is provided (recurring shooting, cal_username already known),
 * sets director_id on the booking at creation.
 */
export async function createTempBooking(
  params: CreateTempBookingParams
): Promise<TempBookingResult> {
  const {
    userId,
    slotStart,
    productId,
    eventTypeSlug,
    locationId,
    existingShootingId,
    metadata,
    directorId,
  } = params;
  const product = await getProductById(productId);
  const durationMinutes = product?.shooting_duration_minutes || 180;
  const startTime = new Date(slotStart);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
  const productName = product?.name || null;

  const tempBookingUid = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;

  const tempBookingResult = await createBookingInDb({
    user_id: userId,
    booking_uid: tempBookingUid,
    start_time: startTime.toISOString(),
    end_time: endTime.toISOString(),
    event_type_slug: eventTypeSlug,
    plan: productName,
    product_id: productId,
    status: 'pending',
    location_id: locationId || null,
    meeting_type: 'shooting' as any,
    shooting_id: existingShootingId || null,
    metadata: { productId, ...metadata },
    ...(directorId != null && directorId !== '' && { director_id: directorId })
  });

  if (!tempBookingResult.success || !tempBookingResult.data) {
    console.error('❌ Erreur lors de la création temporaire du booking:', tempBookingResult.error);
    return {
      success: false,
      error: {
        success: false,
        error: 'Erreur lors de la création de la réservation',
      }
    };
  }

  return {
    success: true,
    bookingId: tempBookingResult.data.id,
    tempBookingUid
  };
}

/**
 * Confirms a booking after successful Cal.com API response
 */
export async function confirmBookingAfterCalResponse(
  tempBookingUid: string,
  calBookingUid: string,
  bookingId: string,
  userId: string,
  calData: any
): Promise<{ shootingId?: string; directorName?: string; directorAvatar?: string }> {
  await updateBookingByUid(tempBookingUid, {
    booking_uid: calBookingUid,
    status: 'confirmed'
  });

  // Get shooting_id from booking (created automatically by createBooking)
  let shootingId: string | undefined;
  const bookingResult = await getBookingById(bookingId);
  if (bookingResult.success && bookingResult.data) {
    shootingId = (bookingResult.data as any).shooting_id || undefined;
    if (shootingId) {
      console.log('✅ Shooting ID from booking:', shootingId);
    } else {
      console.warn('⚠️ No shooting_id found on booking, shooting should have been created automatically');
    }
  }

  let directorName: string | undefined;
  let directorAvatar: string | undefined;

  if (userId && calData) {
    const syncResult = await syncStaffFromCalBooking(
      calData,
      bookingId,
      userId
    );

    if (syncResult.success) {
      directorName = syncResult.directorName;
      directorAvatar = syncResult.directorAvatar;
    }
  }

  return { shootingId, directorName, directorAvatar };
}

/**
 * Handles rescheduling for legacy Calendly bookings by creating a new booking
 */
export async function handleCalendlyReschedule(
  booking: BookingWithCalendlyFields,
  newStart: string,
  createBookingFn: (
    slotStart: string,
    userName: string,
    userEmail: string,
    productId: string,
    userId: string,
    locationId?: string,
    existingBooking?: { booking_calendly_uri?: string | null; shooting_id?: string | null; booking_uid?: string | null } | null
  ) => Promise<CreateBookingResponse>
): Promise<RescheduleBookingResponse> {
  console.log('%c🟡 REPROGRAMMATION CALENDLY - Création nouvelle réservation', 'color: #f59e0b; font-weight: bold; font-size: 12px');

  // Get user info
  const userResult = await getUserById(booking.user_id);
  if (!userResult.success || !userResult.data) {
    return {
      success: false,
      error: 'Impossible de récupérer les informations utilisateur',
    };
  }

  const user = userResult.data;
  let userName = user.email?.split('@')[0] || 'User';
  const userEmail = user.email || '';

  // Get user profile for full name
  const profileResult = await getUserProfile(booking.user_id);
  if (profileResult.success && profileResult.data) {
    const profile = profileResult.data;
    const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
    if (fullName) {
      userName = fullName;
    }
  }

  if (!booking.product_id) {
    return {
      success: false,
      error: 'Produit introuvable pour cette réservation',
    };
  }

  // Create new booking with same shooting_id, passing old booking info
  const createResult = await createBookingFn(
    newStart,
    userName,
    userEmail,
    booking.product_id,
    booking.user_id,
    booking.location_id || undefined,
    {
      booking_calendly_uri: booking.booking_calendly_uri,
      shooting_id: booking.shooting_id,
      booking_uid: booking.booking_uid,
    }
  );

  if (!createResult.success) {
    return {
      success: false,
      error: createResult.error || 'Erreur lors de la création de la nouvelle réservation',
    };
  }

  console.log('%c✅ NOUVELLE RÉSERVATION CRÉÉE (remplace Calendly)', 'color: #10b981; font-weight: bold; font-size: 12px');

  return {
    success: true,
    bookingUid: createResult.bookingUid || '',
  };
}

/**
 * Creates a booking payload for Cal.com API
 */
export async function createBookingPayload(
  params: CreateBookingPayloadParams
): Promise<CreateBookingRequest> {
  const {
    slotStart,
    userName,
    userEmail,
    productId,
    userId,
    eventTypeSlug,
    teamSlug,
    eventTypeId,
    username,
    metadata,
    prsnlBookingId,
  } = params;
  const userResult = await getUserById(userId);
  const isPreparation = metadata?.type === 'preparation';
  let isFirstTimer = true;
  if (userResult.success && userResult.data) {
    const user = userResult.data;
    isFirstTimer = isPreparation ? !user.personal_brand_manager_id : !user.director_id;
  }
  
  const userTimeZone = getUserTimeZone();
  const title = formatBookingTitle(userName, slotStart);
  
  const basePayload = {
    start: slotStart,
    title,
    attendee: {
      name: userName,
      email: userEmail,
      timeZone: userTimeZone,
      language: 'fr',
    },
    metadata: {
      origin: 'app',
      productId,
      ...(prsnlBookingId && { prsnlBookingId }),
      ...metadata,
    },
  };
  
  if (isFirstTimer && eventTypeId) {
    return {
      ...basePayload,
      eventTypeId,
    };
  } else {
    const payload: CreateBookingRequest = {
      ...basePayload,
      eventTypeSlug,
    };
    
    if (username) {
      payload.username = username;
    } else if (teamSlug) {
      payload.teamSlug = teamSlug;
    }
    
    return payload;
  }
}

/**
 * Handles shooting ID logic for non-preparation bookings
 */
function handleShootingBooking(bookingData: any): void {
  const shootingId = bookingData.shooting_id || null;
  if (shootingId) {
    console.log('✅ Shooting ID from booking:', shootingId);
  } else {
    console.warn('⚠️ No shooting_id found on booking, shooting should have been created automatically');
  }
}

/**
 * Handles shooting ID logic for preparation bookings
 */
async function handlePreparationBooking(
  bookingId: string,
  shootingId: string | null | undefined
): Promise<void> {
  if (!shootingId) {
    console.log('⚠️ Aucun shooting_id fourni pour le booking de préparation');
    return;
  }

  console.log('✅ Using provided shooting_id for preparation booking:', shootingId);

  const updateResult = await updateBooking(bookingId, {
    shooting_id: shootingId as any,
  });

  if (updateResult.success) {
    console.log('✅ Booking de préparation lié au shooting');
  } else {
    console.error('❌ Erreur lors de la liaison du booking au shooting:', updateResult.error);
  }
}

/**
 * Syncs staff and returns staff information
 */
async function syncStaffAndGetInfo(
  calBookingData: any,
  bookingId: string,
  userId: string
): Promise<{
  directorName?: string;
  directorAvatar?: string;
  pbmName?: string;
  pbmAvatar?: string;
}> {
  const syncResult = await syncStaffFromCalBooking(
    calBookingData,
    bookingId,
    userId
  );

  if (!syncResult.success) {
    console.warn('⚠️ Échec de la synchronisation du staff:', syncResult.error);
    return {};
  }

  console.log('✅ Staff synchronisé depuis Cal.com:', {
    directorId: syncResult.directorId,
    directorName: syncResult.directorName,
    pbmId: syncResult.pbmId
  });

  return {
    directorName: syncResult.directorName,
    directorAvatar: syncResult.directorAvatar,
    pbmName: syncResult.pbmName,
    pbmAvatar: syncResult.pbmAvatar,
  };
}

/**
 * Saves booking to database and syncs staff
 */
export async function saveBookingAndSyncStaff(
  params: SaveBookingAndSyncStaffParams
): Promise<{
  directorName?: string;
  directorAvatar?: string;
  pbmName?: string;
  pbmAvatar?: string;
}> {
  const {
    calBookingData,
    userId,
    slotStart,
    productId,
    eventTypeSlug,
    locationId,
    meetingUrl,
    shootingId,
  } = params;
  const product = await getProductById(productId);
  const isPreparation = eventTypeSlug.includes('prep') || eventTypeSlug.includes('pbm');
  const durationField = isPreparation
    ? 'preparation_duration_minutes'
    : 'shooting_duration_minutes';
  const durationMinutes = product?.[durationField] || (durationField === 'preparation_duration_minutes' ? 45 : 180);

  const startTime = new Date(slotStart);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

  const productName = product?.name || null;

  // Create booking with meeting_type
  const bookingResult = await createBookingInDb({
    user_id: userId,
    booking_uid: calBookingData.data.uid,
    start_time: startTime.toISOString(),
    end_time: endTime.toISOString(),
    event_type_slug: eventTypeSlug,
    plan: productName,
    product_id: productId,
    status: 'confirmed',
    location_id: locationId || null,
    meeting_type: (isPreparation ? 'editorial' : 'shooting') as any,
    ...(isPreparation && { meeting_url: meetingUrl || null }),
    metadata: isPreparation
      ? { productId, type: 'preparation' }
      : { productId }
  });

  if (!bookingResult.success || !bookingResult.data) {
    console.error('❌ Erreur lors de la création du booking:', bookingResult.error);
    return {};
  }

  console.log('✅ Booking créé dans notre DB:', bookingResult.data.id);

  // Handle shooting_id based on booking type
  if (isPreparation) {
    await handlePreparationBooking(bookingResult.data.id, shootingId);
  } else {
    handleShootingBooking(bookingResult.data as any);
  }

  return syncStaffAndGetInfo(calBookingData, bookingResult.data.id, userId);
}

/**
 * Creates a mock booking in database
 */
export async function createMockBooking(
  params: CreateMockBookingParams
): Promise<void> {
  const {
    userId,
    slotStart,
    productId,
    mockUid,
    eventTypeSlug,
    locationId,
  } = params;
  const startTime = new Date(slotStart);
  
  const product = await getProductById(productId);
  const durationField = eventTypeSlug.includes('prep') || eventTypeSlug.includes('pbm')
    ? 'preparation_duration_minutes'
    : 'shooting_duration_minutes';
  const durationMinutes = product?.[durationField] || (durationField === 'preparation_duration_minutes' ? 45 : 180);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
  
  let location = null;
  if (locationId) {
    const locationResult = await getLocationById(locationId);
    if (locationResult.success && locationResult.data) {
      const locationData = locationResult.data;
      location = `${locationData.address || ''} ${locationData.city || ''}`.trim() || '20 rue Soleillet 75020 Paris';
    }
  } else if (eventTypeSlug.includes('prep') || eventTypeSlug.includes('pbm')) {
    location = 'https://meet.google.com/abc-defg-hij';
  } else {
    location = '20 rue Soleillet 75020 Paris';
  }
  
  await createBookingInDb({
    user_id: userId,
    booking_uid: mockUid,
    start_time: startTime.toISOString(),
    end_time: endTime.toISOString(),
    event_type_slug: eventTypeSlug,
    plan: null,
    product_id: productId,
    status: 'confirmed',
    location_id: locationId || null,
    meeting_url: location,
    metadata: eventTypeSlug.includes('prep') || eventTypeSlug.includes('pbm')
      ? { type: 'preparation', productId }
      : { productId }
  });
}

